import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    fetchTiles,
    patchTile,
    uploadTileImage,
} from "@/lib/api";
import { PageHeader } from "../_shared";

export default function TilesTab() {
    const [tiles, setTiles] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const refresh = useCallback(async () => setTiles(await fetchTiles()), []);
    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <div data-testid="tiles-tab">
            <PageHeader
                title="City"
                accent="Sphere"
                description="Upload an image, set a label, tweak width / height. Changes go live the moment you save."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-line border border-line rounded-2xl overflow-hidden">
                {tiles.map((t) => (
                    <SlotCard
                        key={t.id}
                        tile={t}
                        open={editingId === t.id}
                        onToggle={() =>
                            setEditingId(editingId === t.id ? null : t.id)
                        }
                        onSaved={async () => {
                            setEditingId(null);
                            await refresh();
                            toast("Tile updated.");
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

function SlotCard({ tile, open, onToggle, onSaved }) {
    const [label, setLabel] = useState(tile.label || "");
    const [w, setW] = useState(tile.width);
    const [h, setH] = useState(tile.height);
    const [imageUrl, setImageUrl] = useState(tile.image_url || "");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        setLabel(tile.label || "");
        setW(tile.width);
        setH(tile.height);
        setImageUrl(tile.image_url || "");
    }, [tile, open]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const res = await uploadTileImage(file);
            setImageUrl(res.url);
            toast("Image uploaded.");
        } catch (err) {
            toast(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    const onSave = async () => {
        setBusy(true);
        try {
            await patchTile(tile.id, {
                label,
                image_url: imageUrl || null,
                width: Number(w),
                height: Number(h),
            });
            await onSaved?.();
        } catch (err) {
            toast(err?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    const origin = window.location.origin;
    const thumb = imageUrl
        ? imageUrl.startsWith("/")
            ? `${origin}${imageUrl}`
            : imageUrl
        : null;

    return (
        <div
            data-testid={`slot-card-${tile.slot_index}`}
            className="bg-background"
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.04] transition-colors"
            >
                <div className="w-16 h-16 shrink-0 border border-line bg-white/[0.03] overflow-hidden grid place-items-center rounded-xl">
                    {thumb ? (
                        <img
                            src={thumb}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <span className="text-ink-soft text-[0.65rem] font-display tracking-[0.2em]">
                            {String(tile.slot_index + 1).padStart(2, "0")}
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="vc-section-label text-[#5fe3c9]">
                        Slot {String(tile.slot_index + 1).padStart(2, "0")}
                    </div>
                    <div className="font-display font-bold text-lg mt-1 truncate">
                        {tile.label || "Untitled"}
                    </div>
                    <div className="text-ink-soft text-xs mt-0.5">
                        {tile.width}×{tile.height}
                    </div>
                </div>
                <span className="text-ink-soft text-sm">
                    {open ? "▾" : "▸"}
                </span>
            </button>

            {open && (
                <div className="px-5 pb-5 pt-2 border-t border-line bg-black/30 space-y-4">
                    <div className="vc-section-label text-[#5fe3c9]">Label</div>
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        data-testid={`slot-label-${tile.slot_index}`}
                        className="vc-input"
                        placeholder="City name"
                    />
                    <div className="vc-section-label text-[#5fe3c9]">Image</div>
                    <input
                        ref={fileRef}
                        data-testid={`slot-file-${tile.slot_index}`}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onUpload}
                        className="w-full text-xs text-ink-soft file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#f4f4f0] file:text-black file:font-display file:font-bold file:uppercase file:tracking-[0.18em] file:text-[0.7rem] file:rounded-full hover:file:bg-gradient-cyan hover:file:text-[#0a0a0a]"
                    />
                    <input
                        type="text"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="…or paste image URL"
                        className="vc-input"
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="vc-section-label text-[#5fe3c9]">
                                Width
                            </div>
                            <input
                                type="number"
                                min={20}
                                max={600}
                                value={w}
                                onChange={(e) => setW(e.target.value)}
                                data-testid={`slot-width-${tile.slot_index}`}
                                className="vc-input"
                            />
                        </div>
                        <div>
                            <div className="vc-section-label text-[#5fe3c9]">
                                Height
                            </div>
                            <input
                                type="number"
                                min={20}
                                max={600}
                                value={h}
                                onChange={(e) => setH(e.target.value)}
                                data-testid={`slot-height-${tile.slot_index}`}
                                className="vc-input"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={busy}
                        data-testid={`slot-save-${tile.slot_index}`}
                        className="vc-btn vc-btn-accent vc-btn-lg w-full justify-center disabled:opacity-60"
                    >
                        {busy ? "Saving…" : "Save tile"}
                    </button>
                </div>
            )}
        </div>
    );
}
